import { useState, useMemo, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import {
  FilterTabs,
  PaginationControls,
  SearchBar,
  SortableHeader,
  type SortDirection,
  usePaginatedRows,
  useTableControls,
} from '../components/TableControls';

// ─── Types ────────────────────────────────────────────────────────────────────

type FacilityStatus = 'active' | 'paused';
type UserStatus = 'active' | 'paused' | 'frozen';
type AuditAction = 'pause' | 'freeze' | 'unpause' | 'clawback';
type AuditStatus = 'executed' | 'pending' | 'reverted';
type AuditSortKey = 'action' | 'status' | 'targetId' | 'description' | 'timestamp';

interface Facility {
  id: string;
  name: string;
  pair: string;
  interestRate: string;
  liqThreshold: string;
  tvl: string;
  borrowed: string;
  utilization: string;
  status: FacilityStatus;
}

interface ComplianceUser {
  id: string;
  walletAddress: string;
  status: UserStatus;
}

interface Position {
  id: string;
  userId: string;
  facilityId: string;
  facilityName: string;
  collateral: number;
  borrowed: number;
  healthRatio: number;
  status: string;
}

interface AuditEntry {
  id: string;
  action: AuditAction;
  targetId: string;
  description: string;
  timestamp: string;
  status: AuditStatus;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const INITIAL_FACILITIES: Facility[] = [
  { id: 'facility-1', name: 'USDC Core Facility', pair: 'ABN/USDC', interestRate: '5.5%', liqThreshold: '150%', tvl: '$1,250,000', borrowed: '$750,000', utilization: '60%', status: 'active' },
  { id: 'facility-2', name: 'ABN Yield Facility', pair: 'ABN/USDC', interestRate: '4.2%', liqThreshold: '130%', tvl: '$1,500,000', borrowed: '$900,000', utilization: '60%', status: 'active' },
  { id: 'facility-3', name: 'SNG Stable Facility', pair: 'SNG/USDT', interestRate: '3.8%', liqThreshold: '110%', tvl: '$800,000', borrowed: '$700,000', utilization: '87.5%', status: 'active' },
  { id: 'facility-4', name: 'Institutional ABN Prime', pair: 'APX/USDC', interestRate: 'N/A', liqThreshold: 'N/A', tvl: '$0', borrowed: '$0', utilization: '0%', status: 'paused' },
];

const INITIAL_USERS: ComplianceUser[] = [
  { id: 'user-1', walletAddress: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F', status: 'active' },
  { id: 'user-2', walletAddress: '0x3G2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b', status: 'active' },
  { id: 'user-3', walletAddress: '0x8H2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b', status: 'active' },
  { id: 'user-4', walletAddress: '0x9I2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b', status: 'frozen' },
  { id: 'user-5', walletAddress: '0x0J2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b', status: 'active' },
];

const INITIAL_POSITIONS: Position[] = [
  { id: 'position-1', userId: 'user-1', facilityId: 'facility-1', facilityName: 'USDC Core Facility', collateral: 10000, borrowed: 7500, healthRatio: 1.33, status: 'at-risk' },
  { id: 'position-2', userId: 'user-2', facilityId: 'facility-2', facilityName: 'WBTC Yield Facility', collateral: 50000, borrowed: 25000, healthRatio: 2.0, status: 'healthy' },
  { id: 'position-5', userId: 'user-3', facilityId: 'facility-4', facilityName: 'High Risk Alt Facility', collateral: 1000, borrowed: 800, healthRatio: 1.25, status: 'at-risk' },
];

const TOKENS = ['AGF', 'RETT', 'USDC', 'USDT', 'USD1', 'EURC'];

const INITIAL_AUDIT_LOG: AuditEntry[] = [
  { id: 'action-1', action: 'pause', targetId: 'facility-4', description: 'High volatility in underlying asset (UNI) detected', timestamp: '12/01/2025 14:30', status: 'executed' },
  { id: 'action-2', action: 'freeze', targetId: 'user-4', description: 'Suspicious transaction pattern flagged by AML bot', timestamp: '13/01/2025 09:15', status: 'executed' },
  { id: 'action-3', action: 'unpause', targetId: 'facility-2', description: 'Maintenance window completed', timestamp: '10/01/2025 16:00', status: 'executed' },
  { id: 'action-5', action: 'freeze', targetId: 'user-2', description: 'KYC recertification required', timestamp: '14/01/2025 11:45', status: 'pending' },
  { id: 'action-7', action: 'pause', targetId: 'facility-3', description: 'Emergency circuit breaker test', timestamp: '05/01/2025 08:00', status: 'reverted' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

type ComplianceTab = 'facilities' | 'users' | 'clawback';

const COMPLIANCE_TABS: { value: ComplianceTab; label: string; icon: React.ReactNode }[] = [
  { value: 'facilities', label: 'Facilities', icon: <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-4 w-4" /> },
  { value: 'users', label: 'Users', icon: <Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-4 w-4" /> },
  { value: 'clawback', label: 'Clawback', icon: <Icon name="streamline:interface-arrows-synchronize-arrows-loading-load-sync-synchronize-arrow-reload" className="h-4 w-4" /> },
];

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', className: 'bg-yellow-100 text-yellow-700' },
  frozen: { label: 'Frozen', className: 'bg-red-100 text-red-700' },
};

const AUDIT_ACTION_CONFIG: Record<AuditAction, { label: string; className: string }> = {
  pause: { label: 'PAUSE', className: 'bg-yellow-100 text-yellow-700' },
  freeze: { label: 'FREEZE', className: 'bg-red-100 text-red-700' },
  unpause: { label: 'UNPAUSE', className: 'bg-green-100 text-green-700' },
  clawback: { label: 'CLAWBACK', className: 'bg-purple-100 text-purple-700' },
};

const AUDIT_STATUS_CONFIG: Record<AuditStatus, { label: string; className: string }> = {
  executed: { label: 'Executed', className: 'bg-green-100 text-green-700' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  reverted: { label: 'Reverted', className: 'bg-gray-100 text-gray-600' },
};

function truncateAddress(addr: string): string {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function parseAuditTimestamp(value: string): number {
  const [datePart, timePart] = value.split(' ');
  if (!datePart || !timePart) return 0;
  const [day, month, year] = datePart.split('/').map((part) => Number(part));
  const [hour, minute] = timePart.split(':').map((part) => Number(part));
  return new Date(year, month - 1, day, hour, minute).getTime();
}

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const MAX_VISIBLE_RESULTS = 6;

// ─── Searchable Select ────────────────────────────────────────────────────────

interface SearchableOption {
  id: string;
  label: string;
  sublabel?: string;
  status?: string;
}

function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: SearchableOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder: string;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    if (!query.trim()) return options.slice(0, MAX_VISIBLE_RESULTS);
    const q = query.toLowerCase();
    return options
      .filter((o) => o.label.toLowerCase().includes(q) || o.id.toLowerCase().includes(q) || (o.sublabel?.toLowerCase().includes(q)))
      .slice(0, MAX_VISIBLE_RESULTS);
  }, [options, query]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(id: string) {
    onChange(id);
    setQuery('');
    setOpen(false);
  }

  function handleClear() {
    onChange('');
    setQuery('');
  }

  return (
    <div ref={containerRef} className="relative">
      {selected && !open ? (
        <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm font-medium">{selected.label}</span>
            {selected.sublabel && (
              <span className="text-xs text-muted-foreground">{selected.sublabel}</span>
            )}
            {selected.status && (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CONFIG[selected.status]?.className ?? 'bg-gray-100 text-gray-600'}`}>
                {STATUS_CONFIG[selected.status]?.label ?? selected.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => { setOpen(true); setQuery(''); }}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="streamline:search-visual" className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={handleClear}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Icon name="streamline:search-visual" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className={`${INPUT_CLASS} pl-9`}
          />
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              No results for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <div className="max-h-[240px] overflow-y-auto py-1">
              {filtered.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => handleSelect(opt.id)}
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-accent transition-colors ${value === opt.id ? 'bg-accent/50' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="ml-2 text-xs text-muted-foreground truncate">{opt.sublabel}</span>
                    )}
                  </div>
                  {opt.status && (
                    <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CONFIG[opt.status]?.className ?? 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_CONFIG[opt.status]?.label ?? opt.status}
                    </span>
                  )}
                </button>
              ))}
              {options.length > MAX_VISIBLE_RESULTS && query.trim() === '' && (
                <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                  Type to search {options.length} items...
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page Component ───────────────────────────────────────────────────────────

export function CompliancePage() {
  const [facilities, setFacilities] = useState(INITIAL_FACILITIES);
  const [users, setUsers] = useState(INITIAL_USERS);
  const [auditLog, setAuditLog] = useState(INITIAL_AUDIT_LOG);
  const [activeTab, setActiveTab] = useState<ComplianceTab>('facilities');

  const [selectedFacility, setSelectedFacility] = useState('');
  const [facilityReason, setFacilityReason] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [userReason, setUserReason] = useState('');
  const [clawbackUser, setClawbackUser] = useState('');
  const [clawbackToken, setClawbackToken] = useState('');
  const [clawbackAmount, setClawbackAmount] = useState('');
  const [clawbackReason, setClawbackReason] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState<'all' | AuditAction>('all');
  const [auditStatusFilter, setAuditStatusFilter] = useState<'all' | AuditStatus>('all');
  const [auditSortKey, setAuditSortKey] = useState<AuditSortKey>('timestamp');
  const [auditSortDirection, setAuditSortDirection] = useState<SortDirection>('desc');
  const {
    query: auditQuery,
    setQuery: setAuditQuery,
    page: auditPage,
    setPage: setAuditPage,
    pageSize: auditPageSize,
    setPageSize: setAuditPageSize,
    resetPage: resetAuditPage,
  } = useTableControls();

  const atRiskPositions = useMemo(
    () =>
      INITIAL_POSITIONS.filter((p) => p.healthRatio < 1.5 && p.healthRatio > 0).sort(
        (a, b) => a.healthRatio - b.healthRatio,
      ),
    [],
  );

  const avgHealthFactor = useMemo(() => {
    const validPositions = INITIAL_POSITIONS.filter((p) => p.healthRatio > 0);
    if (validPositions.length === 0) return 0;
    return validPositions.reduce((sum, p) => sum + p.healthRatio, 0) / validPositions.length;
  }, []);

  const filteredAuditLog = useMemo(() => {
    let items = [...auditLog];

    if (auditActionFilter !== 'all') {
      items = items.filter((entry) => entry.action === auditActionFilter);
    }
    if (auditStatusFilter !== 'all') {
      items = items.filter((entry) => entry.status === auditStatusFilter);
    }
    if (auditQuery.trim()) {
      const q = auditQuery.toLowerCase();
      items = items.filter(
        (entry) =>
          entry.id.toLowerCase().includes(q) ||
          entry.targetId.toLowerCase().includes(q) ||
          entry.description.toLowerCase().includes(q) ||
          entry.action.toLowerCase().includes(q) ||
          entry.status.toLowerCase().includes(q),
      );
    }

    return items;
  }, [auditActionFilter, auditStatusFilter, auditQuery, auditLog]);

  const sortedAuditLog = useMemo(() => {
    return [...filteredAuditLog].sort((a, b) => {
      let comparison = 0;

      switch (auditSortKey) {
        case 'action':
          comparison = a.action.localeCompare(b.action);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'targetId':
          comparison = a.targetId.localeCompare(b.targetId);
          break;
        case 'description':
          comparison = a.description.localeCompare(b.description);
          break;
        case 'timestamp':
          comparison = parseAuditTimestamp(a.timestamp) - parseAuditTimestamp(b.timestamp);
          break;
      }

      return auditSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [auditSortDirection, auditSortKey, filteredAuditLog]);

  const { rows: paginatedAuditLog } = usePaginatedRows(sortedAuditLog, auditPage, auditPageSize);

  const actionFilterOptions: { value: 'all' | AuditAction; label: string }[] = [
    { value: 'all', label: 'All Actions' },
    { value: 'pause', label: 'Pause' },
    { value: 'freeze', label: 'Freeze' },
    { value: 'unpause', label: 'Unpause' },
    { value: 'clawback', label: 'Clawback' },
  ];
  const statusFilterOptions: { value: 'all' | AuditStatus; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'executed', label: 'Executed' },
    { value: 'pending', label: 'Pending' },
    { value: 'reverted', label: 'Reverted' },
  ];

  function toggleAuditSort(nextKey: AuditSortKey) {
    setAuditSortDirection((prevDirection) =>
      auditSortKey === nextKey ? (prevDirection === 'asc' ? 'desc' : 'asc') : 'asc',
    );
    setAuditSortKey(nextKey);
    resetAuditPage();
  }

  function addAuditEntry(action: AuditAction, targetId: string, description: string) {
    const entry: AuditEntry = {
      id: `action-${Date.now()}`,
      action,
      targetId,
      description,
      timestamp: new Date().toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }),
      status: 'executed',
    };
    setAuditLog((prev) => [entry, ...prev]);
  }

  function handlePauseFacility() {
    if (!selectedFacility || !facilityReason.trim()) return;
    setFacilities((prev) => prev.map((v) => (v.id === selectedFacility ? { ...v, status: 'paused' as FacilityStatus } : v)));
    addAuditEntry('pause', selectedFacility, facilityReason);
    toast.warning(`Facility paused: ${selectedFacility}`);
    setFacilityReason('');
  }

  function handlePauseUser() {
    if (!selectedUser || !userReason.trim()) return;
    setUsers((prev) => prev.map((u) => (u.id === selectedUser ? { ...u, status: 'paused' as UserStatus } : u)));
    addAuditEntry('pause', selectedUser, userReason);
    toast.warning(`User paused: ${selectedUser}`);
    setUserReason('');
  }

  function handleFreezeUser() {
    if (!selectedUser || !userReason.trim()) return;
    setUsers((prev) => prev.map((u) => (u.id === selectedUser ? { ...u, status: 'frozen' as UserStatus } : u)));
    addAuditEntry('freeze', selectedUser, userReason);
    toast.error(`User frozen: ${selectedUser}`);
    setUserReason('');
  }

  function handleClawback() {
    if (!clawbackUser || !clawbackToken || !clawbackAmount || !clawbackReason.trim()) return;
    addAuditEntry('clawback', clawbackUser, `Clawback ${clawbackAmount} ${clawbackToken}: ${clawbackReason}`);
    toast.success(`Clawback executed: ${clawbackAmount} ${clawbackToken} from ${clawbackUser}`);
    setClawbackAmount('');
    setClawbackReason('');
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:shield-check" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Compliance Controls</h1>
            <p className="text-sm text-muted-foreground">
              Monitor and manage platform compliance and security.
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard title="Total Value Locked" value="$3,550,000" subtitle="Across all facilities" trend={{ label: '↑ 2.5%', className: 'text-green-600' }} />
        <StatCard title="Utilization Rate" value="65.4%" subtitle="Optimal: 80%" trend={{ label: 'Stable', className: 'text-muted-foreground' }} />
        <StatCard title="At-Risk Positions" value={String(atRiskPositions.length)} subtitle="$10,500 value" trend={{ label: 'Action Req', className: 'text-red-600' }} />
        <StatCard title="Avg Health Factor" value={avgHealthFactor.toFixed(2)} subtitle="Global Average" trend={{ label: avgHealthFactor > 1.5 ? 'Healthy' : 'Declining', className: avgHealthFactor > 1.5 ? 'text-green-600' : 'text-yellow-600' }} />
      </div>

      {/* Compliance Actions Card */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle>Compliance Controls</CardTitle>
          <CardDescription>Manage global facility and user compliance status.</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {/* Tab buttons */}
          <div className="mb-6 inline-flex rounded-lg bg-muted p-1">
            {COMPLIANCE_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === tab.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Facilities tab */}
          {activeTab === 'facilities' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Select Facility</label>
                <SearchableSelect
                  options={facilities.map((v) => ({ id: v.id, label: v.name, sublabel: v.pair, status: v.status }))}
                  value={selectedFacility}
                  onChange={setSelectedFacility}
                  placeholder="Search facilities by name or ID..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Reason</label>
                <input type="text" placeholder="Provide reason for action..." value={facilityReason} onChange={(e) => setFacilityReason(e.target.value)} className={INPUT_CLASS} />
              </div>
              <div className="flex gap-3">
                <Button className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white" disabled={!selectedFacility || !facilityReason.trim()} onClick={handlePauseFacility}>
                  Pause Facility
                </Button>
              </div>
            </div>
          )}

          {/* Users tab */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Select User</label>
                <SearchableSelect
                  options={users.map((u) => ({ id: u.id, label: truncateAddress(u.walletAddress), sublabel: u.id, status: u.status }))}
                  value={selectedUser}
                  onChange={setSelectedUser}
                  placeholder="Search users by address or ID..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Reason</label>
                <input type="text" placeholder="Provide reason for action..." value={userReason} onChange={(e) => setUserReason(e.target.value)} className={INPUT_CLASS} />
              </div>
              <div className="flex gap-3">
                <Button className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white" disabled={!selectedUser || !userReason.trim()} onClick={handlePauseUser}>
                  Pause User
                </Button>
                <Button variant="destructive" className="flex-1" disabled={!selectedUser || !userReason.trim()} onClick={handleFreezeUser}>
                  Freeze User
                </Button>
              </div>
            </div>
          )}

          {/* Clawback tab */}
          {activeTab === 'clawback' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Select User</label>
                <SearchableSelect
                  options={users.map((u) => ({ id: u.id, label: truncateAddress(u.walletAddress), sublabel: u.id, status: u.status }))}
                  value={clawbackUser}
                  onChange={setClawbackUser}
                  placeholder="Search users by address or ID..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Token</label>
                <SearchableSelect
                  options={TOKENS.map((t) => ({ id: t, label: t }))}
                  value={clawbackToken}
                  onChange={setClawbackToken}
                  placeholder="Search tokens..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Amount</label>
                <input type="number" placeholder="Enter amount..." value={clawbackAmount} onChange={(e) => setClawbackAmount(e.target.value)} className={INPUT_CLASS} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Reason</label>
                <input type="text" placeholder="Provide reason for clawback..." value={clawbackReason} onChange={(e) => setClawbackReason(e.target.value)} className={INPUT_CLASS} />
              </div>
              <Button variant="destructive" className="w-full" disabled={!clawbackUser || !clawbackToken || !clawbackAmount || !clawbackReason.trim()} onClick={handleClawback}>
                Execute Clawback
              </Button>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Audit Log */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle>Audit Log</CardTitle>
          <CardDescription>History of all compliance actions and interventions.</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <FilterTabs<'all' | AuditAction>
                  filters={actionFilterOptions}
                  active={auditActionFilter}
                  onChange={(value) => {
                    setAuditActionFilter(value);
                    resetAuditPage();
                  }}
                />
                <FilterTabs<'all' | AuditStatus>
                  filters={statusFilterOptions}
                  active={auditStatusFilter}
                  onChange={(value) => {
                    setAuditStatusFilter(value);
                    resetAuditPage();
                  }}
                />
              </div>
              <SearchBar
                value={auditQuery}
                onChange={(value) => {
                  setAuditQuery(value);
                  resetAuditPage();
                }}
                placeholder="Search audit logs..."
                className="w-full sm:w-[320px]"
              />
            </div>

            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left">
                    <SortableHeader<AuditSortKey>
                      label="Action"
                      sortKey="action"
                      activeSortKey={auditSortKey}
                      direction={auditSortDirection}
                      onSort={toggleAuditSort}
                    />
                    <SortableHeader<AuditSortKey>
                      label="Status"
                      sortKey="status"
                      activeSortKey={auditSortKey}
                      direction={auditSortDirection}
                      onSort={toggleAuditSort}
                    />
                    <SortableHeader<AuditSortKey>
                      label="Target"
                      sortKey="targetId"
                      activeSortKey={auditSortKey}
                      direction={auditSortDirection}
                      onSort={toggleAuditSort}
                    />
                    <SortableHeader<AuditSortKey>
                      label="Description"
                      sortKey="description"
                      activeSortKey={auditSortKey}
                      direction={auditSortDirection}
                      onSort={toggleAuditSort}
                    />
                    <SortableHeader<AuditSortKey>
                      label="Timestamp"
                      sortKey="timestamp"
                      activeSortKey={auditSortKey}
                      direction={auditSortDirection}
                      onSort={toggleAuditSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {paginatedAuditLog.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground">
                        No audit entries match your filters.
                      </td>
                    </tr>
                  ) : (
                    paginatedAuditLog.map((entry) => {
                      const actionConfig = AUDIT_ACTION_CONFIG[entry.action];
                      const statusConfig = AUDIT_STATUS_CONFIG[entry.status];
                      return (
                        <tr key={entry.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="px-3 py-2.5">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${actionConfig.className}`}>
                              {actionConfig.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig.className}`}>
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-xs">{entry.targetId}</td>
                          <td className="px-3 py-2.5">{entry.description}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">{entry.timestamp}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <PaginationControls
              page={auditPage}
              setPage={setAuditPage}
              pageSize={auditPageSize}
              setPageSize={setAuditPageSize}
              totalItems={sortedAuditLog.length}
              label="audit entries"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, trend }: {
  title: string;
  value: string;
  subtitle: string;
  trend: { label: string; className: string };
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-xs sm:text-sm text-muted-foreground">{subtitle}</span>
          <span className={`text-xs font-medium ${trend.className}`}>{trend.label}</span>
        </div>
      </CardContent>
    </Card>
  );
}
